'use client';

import { useEffect, useRef, useState } from "react";
import { FaInfoCircle, FaSyncAlt, FaTimes } from "react-icons/fa";
import { useMouseState } from "../../MouseStateContext";
import styles from "./PrecisionTest.module.css";

const TARGET_CLICKS = 6;
const DOT_SIZE = 22;

export default function PrecisionTest() {
    const { mouse, updateMouse } = useMouseState();
    const [testState, setTestState] = useState("initial");
    const [result, setResult] = useState(
        mouse?.tests?.find((test) => test.name === "PrecisionTest")?.result || "initial"
    );
    const [passAmount, setPassAmount] = useState(0);
    const shellRef = useRef(null);

    const centerPosition = () => {
        const shell = shellRef.current;
        const sw = shell?.clientWidth || 340;
        const sh = shell?.clientHeight || 350;
        return {
            top: `${(sh - DOT_SIZE) / 2}px`,
            left: `${(sw - DOT_SIZE) / 2}px`,
            visible: true,
        };
    };

    const [positions, setPositions] = useState(centerPosition);

    useEffect(() => {
        setPositions(centerPosition());
    }, []);

    const setTestResult = (nextResult) => {
        setResult(nextResult);
        updateMouse({
            mouse: {
                ...mouse,
                messageShown: nextResult === "initial" ? false : mouse.messageShown,
                tests: mouse.tests.map((test) =>
                    test.name === "PrecisionTest" ? { ...test, result: nextResult } : test
                ),
            },
        });
    };

    const resetTest = () => {
        setTestState("initial");
        setPassAmount(0);
        setPositions(centerPosition());
        setTestResult("initial");
    };

    const failTest = () => setTestResult("failed");

    const getRandomPoint = (e) => {
        e.stopPropagation();
        if (result === "failed" || result === "success") return;

        const shell = shellRef.current;
        const sw = shell?.clientWidth || 340;
        const sh = shell?.clientHeight || 350;
        const nextCount = passAmount + 1;

        setPositions({
            top: `${Math.max(0, Math.random() * (sh - DOT_SIZE))}px`,
            left: `${Math.max(0, Math.random() * (sw - DOT_SIZE))}px`,
            visible: true,
        });
        setTestState("started");
        setPassAmount(nextCount);

        if (nextCount >= TARGET_CLICKS) {
            setPositions((prev) => ({ ...prev, visible: false }));
            setTestState("end");
            setTestResult("success");
        }
    };

    const feedbackClass =
        result === "success" ? styles.isSuccess : testState === "started" ? styles.isActive : "";
    const remaining = TARGET_CLICKS - passAmount;

    return (
        <div className={styles.wrapper}>
            <h2>Mouse Accuracy Test</h2>
            <div className={styles.body}>
                <div className={styles.actionsRow}>
                    <span className={`${styles.statusPill} ${styles[`status-${result}`]}`}>
                        {result === "success" ? "Success" : result === "failed" ? "Failed" : "In progress"}
                    </span>
                    <div>
                        <button type="button" className="btn btn-sm btn-warning mx-1" onClick={resetTest}>
                            <FaSyncAlt /> Reset
                        </button>
                        <button type="button" className="btn btn-sm btn-danger mx-1" onClick={failTest}>
                            <FaTimes /> Fail
                        </button>
                    </div>
                </div>
                <div className={styles.info}>
                    <FaInfoCircle aria-hidden="true" /> Click the red target {TARGET_CLICKS} times to pass.
                </div>
                <div className={styles.progressDots} aria-label={`${passAmount} of ${TARGET_CLICKS} clicks`}>
                    {Array.from({ length: TARGET_CLICKS }, (_, i) => (
                        <span
                            key={i}
                            className={`${styles.progressDot} ${i < passAmount ? styles.isFilled : ""} ${
                                result === "success" ? styles.isSuccessDot : ""
                            }`}
                        />
                    ))}
                </div>
                <div className={`${styles.feedback} ${feedbackClass}`}>
                    {result === "success" && `Target hit ${TARGET_CLICKS} times — test passed!`}
                    {result === "failed" && "Test marked as failed."}
                    {testState === "initial" && result === "initial" && "Pending — click the target to begin"}
                    {testState === "started" &&
                        result === "initial" &&
                        `${remaining} click${remaining === 1 ? "" : "s"} remaining`}
                </div>
                <div
                    className={`${styles.shell} ${styles[`result-${result}`]}`}
                    onClick={failTest}
                    ref={shellRef}
                    role="presentation"
                >
                    <div className={styles.targetRing} aria-hidden="true" />
                    {testState === "started" && result === "initial" && (
                        <span className={styles.clicksLabel}>
                            {passAmount}/{TARGET_CLICKS} hits
                        </span>
                    )}
                    {positions.visible && (
                        <button
                            type="button"
                            aria-label="Accuracy target"
                            className={`${styles.runner} ${
                                result === "success" ? styles.runnerSuccess : styles.runnerDanger
                            }`}
                            onClick={getRandomPoint}
                            style={{ top: positions.top, left: positions.left }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
